import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  Box,
  Tooltip,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress
} from '@mui/material';
import { toast } from 'react-toastify';

import StorageFactory from '../../Util/StorageFactory';
import {
  ACCESS_RESTRICTED_MESSAGE,
  APPOINTMENTS,
  ROUTE_ENQUIRY_DETAILS,
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
  SESSION_USER_ROLES_PERMISSIONS
} from '../../Util/VMConstants';
import { API_GET_APPT_FOLLOWUPSList } from '../../Util/URLConstants';
import {
  checkIsPermissionExist,
  displayFormattedDateTime,
  isEmpty,
  renderSNo,
  renderWithTooltip
} from '../../Util/UtilHelper';

import Loader from '../../Components/Loader';
import { align_left, align_right, align_row } from '../../Styles/VMStyles';
import MUITypography from '../../Components/MUITypography';
import MuiDatagrid from '../../Components/MUIDataGrid';
import NoPermission from '../../Components/NoPermission';
import ReusableDataGrid from '../../Components/ReusableDataGrid';

function VMENQAppointments({ enquiry_id }) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);

  const typeFromQuery = query.get('type') || APPOINTMENTS;
  const dateFromQuery = query.get('date');

  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const VM_PERMISSIONS = sessionFactory.get(SESSION_USER_ROLES_PERMISSIONS);
  const currencySymbol = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || '₹';

  /* ---------------- STATE ---------------- */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [total, setTotal] = useState(0);

  const [todayOnly, setTodayOnly] = useState(!isEmpty(dateFromQuery));
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  /* ---------------- FETCH DATA ---------------- */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        type: typeFromQuery,
        page: page + 1,
        pageSize
      };

      if (!isEmpty(enquiry_id)) {
        params.enquiry_id = enquiry_id;
      } else {
        params.user_id = userID;
      }

      if (todayOnly) {
        const baseDate = dateFromQuery || dayjs().format('YYYY-MM-DD');

        params.start_date = dayjs(baseDate, 'YYYY-MM-DD')
          .startOf('day')
          .toISOString();

        params.end_date = dayjs(baseDate, 'YYYY-MM-DD')
          .add(1, 'day')
          .startOf('day')
          .toISOString();
      }

      if (!todayOnly && startDate && endDate) {
        params.start_date = dayjs(startDate, 'YYYY-MM-DD')
          .startOf('day')
          .toISOString();

        params.end_date = dayjs(endDate, 'YYYY-MM-DD')
          .add(1, 'day')
          .startOf('day')
          .toISOString();
      }

      const response = await axios.get(API_GET_APPT_FOLLOWUPSList, { params });
      const respData = response.data || {};

      if (respData.status_code === 200) {
        const formattedRows = (respData.data || []).map((item, idx) => ({
          ...item,
          s_no: idx + 1 + page * pageSize,
          id: item.id || item.enquiry_id || `row-${idx}`
        }));

        setItems(formattedRows);
        setTotal(respData.pagination?.total || 0);
      } else {
        toast.error(respData.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [typeFromQuery, enquiry_id, page, pageSize, userID, todayOnly, startDate, endDate, dateFromQuery]);

  useEffect(() => {
    setPage(0);
  }, [typeFromQuery, enquiry_id, todayOnly, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------------- COLUMNS ---------------- */


  const columns = [
    renderSNo(),
    {
      field: 'enquiry_id',
      headerName: 'Enquiry ID',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <MUITypography
            sx={{ ...align_left,height: '100%',
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
            }}
            onClick={() =>
              navigate('/' + ROUTE_ENQUIRY_DETAILS, { state: { enquiry_id: params.value } })
            }
          >
            {params.value}
          </MUITypography>
        </Tooltip>
      ),
    },
    { field: 'date_time', headerName: 'Date & Time', flex: 2,
      renderCell: (p) => renderWithTooltip(displayFormattedDateTime(p.value, currencySymbol, { wallClock: true })) },
    { field: 'notes', headerName: 'Notes', flex: 2,
      renderCell: (p) => renderWithTooltip(p.value) },
    { field: 'name', headerName: 'Name', flex: 1,
      renderCell: (p) => renderWithTooltip(p.value) },
    { field: 'created_date', headerName: 'Created Date', flex: 2,
      renderCell: (p) => renderWithTooltip(displayFormattedDateTime(p.value, currencySymbol)) }
  ];

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ p: 2 ,
     ...(location?.pathname?.includes('appointmentsfollwups') ? { ml: { md: 0 } } : {}) }}>
      
      <Box sx={align_row}>
        <Box sx={align_left}>
          <MUITypography
            label={typeFromQuery === APPOINTMENTS ? 'Appointments' : 'Follow-ups'}
            variant="h4"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Box sx={align_right}>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={todayOnly}
                  onChange={(e) => {
                    setTodayOnly(e.target.checked);
                    setStartDate(null);
                    setEndDate(null);
                  }}
                />
              }
              label="Today"
            />

            {!todayOnly && (
              <>
                <TextField type="date" label="From" size="small"
                  InputLabelProps={{ shrink: true }}
                  value={startDate || ''}
                  onChange={(e) => setStartDate(e.target.value)}
                />

                <TextField type="date" label="To" size="small"
                  InputLabelProps={{ shrink: true }}
                  value={endDate || ''}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </>
            )}

            <Button variant="outlined" onClick={() => {
              setTodayOnly(false);
              setStartDate(null);
              setEndDate(null);
            }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>

      {checkIsPermissionExist(VM_PERMISSIONS, 'VM010') ? (
        <ReusableDataGrid
          rows={items}
          columns={columns}
          loading={loading}
          rowCount={total}
          paginationMode="server"

          paginationModel={{ page, pageSize }}                         // 👈 controlled pagination

          onPaginationModelChange={(model) => {                       // 👈 arrows + page-size working
            if (model.page !== page) setPage(model.page);
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(0);                                             // reset to first page on size change
            }
          }}

          getRowId={(row) => row.id}
        />

      ) : (
        <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />  
      )}
    </Box>
  );
}

export default VMENQAppointments;
